import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Trash2, Plus, ArrowUp, ArrowDown, ListTree } from "lucide-react";

const ACTIONS = [
  { value: "list_plans", label: "Listar nossos planos" },
  { value: "send_boleto", label: "Enviar 2ª via de boleto" },
  { value: "boleto_outro", label: "2ª via de OUTRA pessoa (pede o CPF do titular)" },
  { value: "send_pix", label: "Gerar PIX EFI" },
  { value: "open_ticket", label: "Abrir chamado de suporte" },
  { value: "plan_info", label: "Consultar plano do cliente" },
  { value: "send_text", label: "Responder com texto" },
  { value: "human_handoff", label: "Transferir para atendente" },
  { value: "new_registration", label: "Cadastro de novo cliente" },
];

export default function MenuBuilder() {
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [savingSubjects, setSavingSubjects] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/chatbot/menu");
      setMenu(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
    try {
      const { data } = await api.get("/tenant/ticket-subjects");
      setSubjects(Array.isArray(data) ? data : []);
    } catch (ex) {
      // silencioso
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (!menu) return null;

  const updateSubject = (i, patch) =>
    setSubjects((s) => s.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeSubject = (i) => setSubjects((s) => s.filter((_, idx) => idx !== i));
  const addSubject = () =>
    setSubjects((s) => [...s, { key: String(s.length + 1), icon: "🔧", label: "Novo assunto" }]);
  const moveSubject = (i, dir) => {
    setSubjects((s) => {
      const arr = [...s];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return s;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const saveSubjects = async () => {
    setSavingSubjects(true);
    try {
      await api.put("/tenant/ticket-subjects", { subjects });
      toast.success("Assuntos salvos");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSavingSubjects(false);
    }
  };

  const updateOpt = (i, patch) =>
    setMenu((m) => ({
      ...m,
      options: m.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)),
    }));
  const removeOpt = (i) =>
    setMenu((m) => ({ ...m, options: m.options.filter((_, idx) => idx !== i) }));
  const addOpt = () =>
    setMenu((m) => ({
      ...m,
      options: [
        ...m.options,
        { key: String(m.options.length + 1), label: "Nova opção", action: "send_text", response_text: "" },
      ],
    }));
  const move = (i, dir) => {
    setMenu((m) => {
      const arr = [...m.options];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return m;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...m, options: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/chatbot/menu", menu);
      toast.success("Menu salvo");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Bot flow</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Menu do Chatbot</h1>
        <p className="text-sm text-slate-500 mt-2">
          Construa o menu numerado que o bot responde no WhatsApp. Cada opção pode disparar uma
          ação (2ª via, PIX, atendente ou texto livre).
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-2 space-y-5">
          <div className="text-xs text-slate-500 bg-slate-950/50 border border-slate-800 rounded-md p-3">
            A saudação inicial e a resposta para opção inválida agora são editadas na tela{" "}
            <span className="text-emerald-400 font-medium">Templates</span> ("Menu inicial (chatbot)" e
            "Resposta para opção inválida (chatbot)"). Aqui você edita só as opções numeradas abaixo.
          </div>

          <div className="border-t border-slate-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Opções</div>
              <Button
                data-testid="menu-add-opt-btn"
                onClick={addOpt}
                variant="outline"
                size="sm"
                className="border-slate-700 hover:bg-slate-800 hover:text-white"
              >
                <Plus className="w-3 h-3 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-3">
              {menu.options.map((o, i) => (
                <div
                  key={i}
                  data-testid={`menu-opt-row-${i}`}
                  className="border border-slate-800 rounded-md p-4 bg-slate-950/50 space-y-3"
                >
                  <div className="grid grid-cols-12 gap-2">
                    <Input
                      className="col-span-2 bg-slate-950 border-slate-800 text-white font-mono-jb focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
                      placeholder="1"
                      value={o.key}
                      data-testid={`menu-opt-key-${i}`}
                      onChange={(e) => updateOpt(i, { key: e.target.value })}
                    />
                    <Input
                      className="col-span-6 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
                      placeholder="Texto exibido"
                      value={o.label}
                      data-testid={`menu-opt-label-${i}`}
                      onChange={(e) => updateOpt(i, { label: e.target.value })}
                    />
                    <div className="col-span-4">
                      <Select
                        value={o.action}
                        onValueChange={(v) => updateOpt(i, { action: v })}
                      >
                        <SelectTrigger
                          data-testid={`menu-opt-action-${i}`}
                          className="bg-slate-950 border-slate-800 text-white"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                          {ACTIONS.map((a) => (
                            <SelectItem key={a.value} value={a.value}>
                              {a.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {(o.action === "send_text" || o.action === "human_handoff") && (
                    <Textarea
                      rows={2}
                      placeholder="Texto da resposta"
                      value={o.response_text}
                      data-testid={`menu-opt-text-${i}`}
                      onChange={(e) => updateOpt(i, { response_text: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
                    />
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => move(i, -1)}
                      className="p-1 rounded text-slate-500 hover:text-emerald-400"
                      title="Subir"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => move(i, 1)}
                      className="p-1 rounded text-slate-500 hover:text-emerald-400"
                      title="Descer"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <div className="flex-1" />
                    <button
                      data-testid={`menu-opt-remove-${i}`}
                      onClick={() => removeOpt(i)}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              data-testid="menu-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Salvar menu
            </Button>
          </div>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 p-6">
          <div className="flex items-center gap-2">
            <ListTree className="w-4 h-4 text-emerald-400" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Prévia WhatsApp</div>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950 p-4 min-h-[300px]">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-slate-100 text-sm p-3 whitespace-pre-wrap leading-relaxed">
              <span className="text-slate-500 italic">(saudação definida em Templates)</span>
              {"\n\n"}
              {menu.options.map((o) => `${o.key} - ${o.label}`).join("\n")}
            </div>
          </div>
        </Card>
      </section>

      {/* ====== Assuntos de Chamado ====== */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-2 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Suporte</div>
            <h2 className="font-outfit text-2xl font-bold text-white mt-1">Assuntos de Chamado</h2>
            <p className="text-sm text-slate-500 mt-1">
              Menu exibido quando o cliente escolhe "Abrir chamado". A opção com chave <span className="font-mono-jb text-emerald-400">0</span> vira "Falar com atendente".
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Opções</div>
            <Button
              data-testid="ticket-subject-add-btn"
              onClick={addSubject}
              variant="outline"
              size="sm"
              className="border-slate-700 hover:bg-slate-800 hover:text-white"
            >
              <Plus className="w-3 h-3 mr-1" /> Adicionar
            </Button>
          </div>
          <div className="space-y-3">
            {subjects.map((s, i) => (
              <div
                key={i}
                data-testid={`ticket-subject-row-${i}`}
                className="border border-slate-800 rounded-md p-4 bg-slate-950/50"
              >
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    className="col-span-2 bg-slate-950 border-slate-800 text-white font-mono-jb text-center"
                    placeholder="1"
                    value={s.key}
                    data-testid={`ticket-subject-key-${i}`}
                    onChange={(e) => updateSubject(i, { key: e.target.value })}
                  />
                  <Input
                    className="col-span-2 bg-slate-950 border-slate-800 text-white text-center"
                    placeholder="🔧"
                    value={s.icon || ""}
                    data-testid={`ticket-subject-icon-${i}`}
                    onChange={(e) => updateSubject(i, { icon: e.target.value })}
                  />
                  <Input
                    className="col-span-6 bg-slate-950 border-slate-800 text-white"
                    placeholder="Rótulo (ex: Internet lenta)"
                    value={s.label}
                    data-testid={`ticket-subject-label-${i}`}
                    onChange={(e) => updateSubject(i, { label: e.target.value })}
                  />
                  <div className="col-span-2 flex items-center gap-1 justify-end">
                    <button onClick={() => moveSubject(i, -1)} className="p-1 text-slate-500 hover:text-emerald-400" title="Subir">
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveSubject(i, 1)} className="p-1 text-slate-500 hover:text-emerald-400" title="Descer">
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`ticket-subject-remove-${i}`}
                      onClick={() => removeSubject(i)}
                      className="p-1 text-red-400 hover:text-red-300"
                      title="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button
              data-testid="ticket-subject-save-btn"
              onClick={saveSubjects}
              disabled={savingSubjects}
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Salvar assuntos
            </Button>
          </div>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 p-6">
          <div className="flex items-center gap-2">
            <ListTree className="w-4 h-4 text-emerald-400" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Prévia Chamado</div>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950 p-4 min-h-[240px]">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-slate-100 text-sm p-3 whitespace-pre-wrap leading-relaxed">
              {"⚒️ ABERTURA DE CHAMADO TÉCNICO"}
              {"\n"}Qual o problema que você está enfrentando?
              {"\n"}📌 Selecione uma das opções abaixo:
              {"\n"}
              {subjects.map((s) => `${s.key || "?"}️⃣ - ${s.icon || ""} ${s.label || ""}`.trim()).join("\n")}
              {"\n\nDigite o número da opção desejada:"}
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}
